import { PlaceAction, PlaceActionType } from '../../actions';
import { PlaceEntry, PlaceStatus, PlacesState } from '../../state';
import produce, { Draft } from 'immer';
import { serializeErrors } from '../serialize-errors';

const initialState: PlacesState = Object.freeze({});

const emptyPlace: PlaceEntry['place'] = Object.freeze({
  categoryIds: [],
  description: '',
  id: '',
  locations: [],
  name: '',
  pageId: null,
  pageTitle: null,
  posterImages: [],
  tagIds: [],
  tags: '',
});

const emptyEntry: PlaceEntry = Object.freeze({
  errors: null,
  place: emptyPlace,
  status: null,
});

const getPlace = <A extends { placeKey: string }>(
  state: Draft<PlacesState>,
  action: A,
): Draft<PlaceEntry> => {
  const entry = state[action.placeKey];
  if (entry) {
    return entry;
  } else {
    throw new Error('Place not in state.');
  }
};

const setPlaceStatus = <A extends { errors?: Error[]; placeKey: string }>(
  state: Draft<PlacesState>,
  action: A,
  status: PlaceStatus,
): void => {
  const entry = getPlace(state, action);
  entry.status = status;
  if (action.errors) {
    entry.errors = serializeErrors(action.errors);
  } else {
    entry.errors = null;
  }
};

export const placesReducer = (
  state: PlacesState = initialState,
  action: PlaceAction,
): PlacesState =>
  produce(state, (draft): void => {
    switch (action.type) {
      case PlaceActionType.CreatePlaceError:
        return setPlaceStatus(draft, action, PlaceStatus.CreateError);

      case PlaceActionType.CreatePlaceRequest:
        return setPlaceStatus(draft, action, PlaceStatus.CreateRequested);

      case PlaceActionType.CreatePlaceSuccess:
        return setPlaceStatus(draft, action, PlaceStatus.CreateSuccess);

      case PlaceActionType.DeletePlaceError:
        return setPlaceStatus(draft, action, PlaceStatus.DeleteError);

      case PlaceActionType.DeletePlaceRequest:
        return setPlaceStatus(draft, action, PlaceStatus.DeleteRequested);

      case PlaceActionType.DeletePlaceSuccess:
        return setPlaceStatus(draft, action, PlaceStatus.DeleteSuccess);

      case PlaceActionType.LoadPlace:
        draft[action.placeKey] = {
          ...emptyEntry,
          place: {
            ...emptyPlace,
            ...action.place,
          },
        };
        return;

      case PlaceActionType.LoadPlaceError:
        return setPlaceStatus(draft, action, PlaceStatus.LoadError);

      case PlaceActionType.LoadPlaceRequest:
        draft[action.placeKey] = { ...emptyEntry };
        return setPlaceStatus(draft, action, PlaceStatus.LoadRequested);

      case PlaceActionType.LoadPlaceSuccess:
        draft[action.placeKey].place = {
          ...emptyPlace,
          ...action.place,
        };
        return setPlaceStatus(draft, action, PlaceStatus.LoadSuccess);

      case PlaceActionType.SetPlaceCategoryIds:
        getPlace(draft, action).place.categoryIds = action.categoryIds;
        return;

      case PlaceActionType.SetPlaceDescription:
        getPlace(draft, action).place.description = action.description;
        return;

      case PlaceActionType.SetPlaceName:
        getPlace(draft, action).place.name = action.name;
        return;

      case PlaceActionType.SetPlaceLocation:
        getPlace(draft, action).place.locations = [action.location];
        return;

      case PlaceActionType.SetPlacePageId:
        getPlace(draft, action).place.pageId = action.pageId;
        return;

      case PlaceActionType.SetPlacePageTitle:
        getPlace(draft, action).place.pageTitle = action.title;
        return;

      case PlaceActionType.SetPlacePosterImages:
        getPlace(draft, action).place.posterImages = action.posterImages;
        return;

      case PlaceActionType.SetPlaceStatus:
        getPlace(draft, action).status = action.status;
        return;

      case PlaceActionType.SetPlaceTagIds:
        getPlace(draft, action).place.tagIds = action.tagIds;
        return;

      case PlaceActionType.SetPlaceTags:
        getPlace(draft, action).place.tags = action.tags;
        return;

      // TODO: this may need to be handled with an epic to add a delay between
      // when the unload is requested and when it is accomplished to avoid
      // disappearing UI state.
      case PlaceActionType.UnloadPlace:
        delete draft[action.placeKey];
        return;

      case PlaceActionType.UpdatePlaceError:
        return setPlaceStatus(draft, action, PlaceStatus.UpdateError);

      case PlaceActionType.UpdatePlaceRequest:
        return setPlaceStatus(draft, action, PlaceStatus.UpdateRequested);

      case PlaceActionType.UpdatePlaceSuccess:
        return setPlaceStatus(draft, action, PlaceStatus.UpdateSuccess);
    }
  });
